import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { SketchPicker } from 'react-color';

const ColorPickerField = ({ label, color, onChange }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const swatchRef = useRef(null);
  const pickerRef = useRef(null);

  // Handle click outside to close the picker
  const handleClickOutside = useCallback((event) => {
    // Check if click is on the swatch
    if (swatchRef.current && swatchRef.current.contains(event.target)) {
      return;
    }
    // Check if click is on the picker
    if (pickerRef.current && pickerRef.current.contains(event.target)) {
      return;
    }
    setIsVisible(false);
  }, []);

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, handleClickOutside]);

  const handleColorChange = (newColor) => {
    onChange(newColor.hex);
  };

  const openPicker = () => {
    if (swatchRef.current) {
      const rect = swatchRef.current.getBoundingClientRect();
      // Position below the swatch, but ensure it doesn't go off-screen
      let top = rect.bottom + 8;
      let left = rect.left;

      // Check if picker would go below viewport
      const pickerHeight = 300; // approximate height of SketchPicker
      if (top + pickerHeight > window.innerHeight) {
        top = rect.top - pickerHeight - 8;
      }

      // Check if picker would go off right edge
      const pickerWidth = 220;
      if (left + pickerWidth > window.innerWidth) {
        left = window.innerWidth - pickerWidth - 16;
      }

      setPickerPosition({ top, left });
    }
    setIsVisible(true);
  };

  const togglePicker = () => {
    if (isVisible) {
      setIsVisible(false);
    } else {
      openPicker();
    }
  };

  // Render the picker in a portal to avoid overflow issues
  const renderPicker = () => {
    if (!isVisible) return null;

    return ReactDOM.createPortal(
      <div
        ref={pickerRef}
        style={{
          position: 'fixed',
          top: pickerPosition.top,
          left: pickerPosition.left,
          zIndex: 10000,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        <SketchPicker
          color={color || '#ffffff'}
          onChangeComplete={handleColorChange}
        />
      </div>,
      document.body
    );
  };

  return (
    <div className="shape-property color-picker-container">
      <label className="shape-property-label" onClick={togglePicker}>
        {label}
      </label>
      <div
        ref={swatchRef}
        className="color-swatch"
        onClick={togglePicker}
        style={{
          backgroundColor: color || '#ffffff',
          width: '32px',
          height: '24px',
          borderRadius: '4px',
          border: '1px solid #d1d5db',
          cursor: 'pointer',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)'
        }}
      />
      {renderPicker()}
    </div>
  );
};

export default ColorPickerField;
